import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { updatePageSEO, SEO_CONFIGS } from '../../utils/seo';

export default function TrialPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    studentName: '',
    studentGrade: '',
    parentName: '',
    phone: '',
    email: '',
    preferredDate: '',
    preferredTime: '',
    subjects: [] as string[],
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    // Update SEO
    updatePageSEO(SEO_CONFIGS.trial);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubjectChange = (subject: string) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.includes(subject)
        ? prev.subjects.filter(s => s !== subject)
        : [...prev.subjects, subject]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('studentName', formData.studentName);
      formBody.append('studentGrade', formData.studentGrade);
      formBody.append('parentName', formData.parentName);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('preferredDate', formData.preferredDate);
      formBody.append('preferredTime', formData.preferredTime);
      formBody.append('subjects', formData.subjects.join(', '));
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5143sqopjgqftil4k40', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          studentName: '',
          studentGrade: '',
          parentName: '',
          phone: '',
          email: '',
          preferredDate: '',
          preferredTime: '',
          subjects: [],
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/80 backdrop-blur-md'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="text-2xl font-bold text-[#2D7A4F]">
              おかやま学習塾
            </Link>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">事業内容</Link>
              <Link to="/about" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">会社概要</Link>
              <Link to="/careers" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">求人情報</Link>
              <Link to="/blog" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">ブログ・新着情報</Link>
              <Link to="/trial" className="text-[#2D7A4F] hover:text-[#236038] transition-colors text-base font-medium whitespace-nowrap border-b-2 border-[#2D7A4F]">体験申し込み</Link>
              <a href="https://line.me/R/ti/p/@okayamajuku" target="_blank" rel="noopener noreferrer" className="bg-[#2D7A4F] text-white px-6 py-2.5 rounded-full hover:bg-[#236038] transition-colors flex items-center space-x-2 text-base font-medium whitespace-nowrap">
                <i className="ri-line-fill text-lg"></i>
                <span>LINE相談</span>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-[#2D7A4F] w-10 h-10 flex items-center justify-center"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="px-6 py-4 space-y-4">
              <Link to="/services" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>事業内容</Link>
              <Link to="/about" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>会社概要</Link>
              <Link to="/careers" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>求人情報</Link>
              <Link to="/blog" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>ブログ・新着情報</Link>
              <Link to="/trial" className="block text-[#2D7A4F] hover:text-[#236038] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>体験申し込み</Link>
              <a href="https://line.me/R/ti/p/@okayamajuku" target="_blank" rel="noopener noreferrer" className="block bg-[#2D7A4F] text-white px-6 py-3 rounded-full hover:bg-[#236038] transition-colors text-center text-base font-medium">
                <i className="ri-line-fill mr-2"></i>LINE相談
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative h-[40vh] min-h-[350px] sm:min-h-[400px] flex items-center justify-center pt-20">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/49e108a0118a7f67241430fd65392d53.jpeg" 
            alt="おかやま学習塾の体験授業"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 text-center px-6">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6">体験申し込み</h1>
          <div className="flex items-center justify-center space-x-3 text-white/90 text-sm">
            <Link to="/" className="hover:text-white transition-colors">ホーム</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>体験申し込み</span>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 sm:py-20 lg:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
              無料体験授業の<br className="sm:hidden" />お申し込み
            </h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto px-4">
              岡山市北区で志望校合格を目指すなら、まずは無料体験授業をお試しください。<br />
              お子様の学習状況を診断し、最適なカリキュラムをご提案します。
            </p>
          </div>

          {/* Benefits */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-[#F1F8F4] rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-[#2D7A4F] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-file-list-3-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">学力診断テスト</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                現在の学力を正確に把握し、最適な学習プランを作成します
              </p>
            </div>

            <div className="bg-[#F1F8F4] rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-[#2D7A4F] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-book-open-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">実際の授業体験</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                個別指導の雰囲気や指導方法を実際に体験できます
              </p>
            </div>

            <div className="bg-[#F1F8F4] rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-[#2D7A4F] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-discuss-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">保護者面談</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                岡山市北区の入試情報や学習プランを詳しくご説明します
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white border-2 border-gray-200 rounded-3xl p-8 lg:p-12">
            <form onSubmit={handleSubmit} data-readdy-form>
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="studentName" className="block text-base font-semibold text-gray-900 mb-2">
                      生徒様のお名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="studentName"
                      name="studentName"
                      value={formData.studentName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2D7A4F] focus:outline-none text-sm"
                      placeholder="山田 太郎"
                    />
                  </div>

                  <div>
                    <label htmlFor="studentGrade" className="block text-base font-semibold text-gray-900 mb-2">
                      学年 <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="studentGrade"
                      name="studentGrade"
                      value={formData.studentGrade}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2D7A4F] focus:outline-none text-sm"
                    >
                      <option value="">選択してください</option>
                      <option value="小学1年生">小学1年生</option>
                      <option value="小学2年生">小学2年生</option>
                      <option value="小学3年生">小学3年生</option>
                      <option value="小学4年生">小学4年生</option>
                      <option value="小学5年生">小学5年生</option>
                      <option value="小学6年生">小学6年生</option>
                      <option value="中学1年生">中学1年生</option>
                      <option value="中学2年生">中学2年生</option>
                      <option value="中学3年生">中学3年生</option>
                      <option value="高校1年生">高校1年生</option>
                      <option value="高校2年生">高校2年生</option>
                      <option value="高校3年生">高校3年生</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="parentName" className="block text-base font-semibold text-gray-900 mb-2">
                    保護者様のお名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="parentName"
                    name="parentName"
                    value={formData.parentName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2D7A4F] focus:outline-none text-sm"
                    placeholder="山田 花子"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-base font-semibold text-gray-900 mb-2">
                      電話番号 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2D7A4F] focus:outline-none text-sm"
                      placeholder="090-1234-5678"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-base font-semibold text-gray-900 mb-2">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2D7A4F] focus:outline-none text-sm"
                      placeholder="example@email.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="preferredDate" className="block text-base font-semibold text-gray-900 mb-2">
                      希望日
                    </label>
                    <input
                      type="date"
                      id="preferredDate"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2D7A4F] focus:outline-none text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="preferredTime" className="block text-base font-semibold text-gray-900 mb-2">
                      希望時間帯
                    </label>
                    <select
                      id="preferredTime"
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2D7A4F] focus:outline-none text-sm"
                    >
                      <option value="">選択してください</option>
                      <option value="15:00-16:00">15:00-16:00</option>
                      <option value="16:00-17:00">16:00-17:00</option>
                      <option value="17:00-18:00">17:00-18:00</option>
                      <option value="18:00-19:00">18:00-19:00</option>
                      <option value="19:00-20:00">19:00-20:00</option>
                      <option value="20:00-21:00">20:00-21:00</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-900 mb-3">
                    希望科目（複数選択可）
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {['国語', '数学・算数', '英語', '理科', '社会'].map((subject) => (
                      <label key={subject} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.subjects.includes(subject)}
                          onChange={() => handleSubjectChange(subject)}
                          className="w-5 h-5 text-[#2D7A4F] border-2 border-gray-300 rounded focus:ring-[#2D7A4F]"
                        />
                        <span className="text-base text-gray-700">{subject}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-base font-semibold text-gray-900 mb-2">
                    ご質問・ご要望
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2D7A4F] focus:outline-none text-sm resize-none"
                    placeholder="お子様の学習状況や気になることなど、お気軽にお書きください（500文字以内）"
                  ></textarea>
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {formData.message.length} / 500文字
                  </div>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border-2 border-green-500 rounded-xl p-4 text-center">
                    <i className="ri-checkbox-circle-line text-3xl text-green-500 mb-2"></i>
                    <p className="text-green-700 font-semibold">お申し込みありがとうございます！</p>
                    <p className="text-green-600 text-sm mt-1">担当者より2営業日以内にご連絡いたします。</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border-2 border-red-500 rounded-xl p-4 text-center">
                    <i className="ri-error-warning-line text-3xl text-red-500 mb-2"></i>
                    <p className="text-red-700 font-semibold">送信に失敗しました</p>
                    <p className="text-red-600 text-sm mt-1">お手数ですが、もう一度お試しください。</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#2D7A4F] text-white px-8 py-4 rounded-full hover:bg-[#236038] transition-all text-base lg:text-lg font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? '送信中...' : '無料体験授業を申し込む'}
                </button>
              </div>
            </form>
          </div>

          {/* LINE Option */}
          <div className="mt-10 sm:mt-12 text-center">
            <p className="text-base text-gray-600 mb-4 sm:mb-6 px-4">
              または、LINEからも<br className="sm:hidden" />お申し込みいただけます
            </p>
            <a href="https://line.me/R/ti/p/@okayamajuku" target="_blank" rel="noopener noreferrer" className="inline-flex items-center bg-[#06C755] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full hover:bg-[#05b34c] transition-all text-base lg:text-lg font-semibold shadow-lg whitespace-nowrap">
              <i className="ri-line-fill text-xl mr-2"></i>
              <span>LINEで申し込む</span>
            </a>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 sm:py-20 lg:py-32 bg-[#A5D6A7]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
            お電話でのお問い合わせ
          </h2>
          <p className="text-base text-gray-700 mb-8 px-4">
            お急ぎの方はお電話でも<br className="sm:hidden" />お問い合わせいただけます
          </p>
          <a href="tel:0860123456" className="inline-flex items-center bg-[#2D7A4F] text-white px-8 py-4 rounded-full hover:bg-[#236038] transition-all text-xl font-semibold shadow-lg whitespace-nowrap">
            <i className="ri-phone-fill text-2xl mr-3"></i>
            <span>086-012-3456</span>
          </a>
          <p className="text-base text-gray-700 mt-6 px-4">
            営業時間：15:00 〜 22:00（日曜日・祝日を除く）
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1B5E3F] text-white mx-6 lg:mx-8 my-8 rounded-3xl">
        <div className="max-w-7xl mx-auto px-8 lg:px-12 py-16">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <h3 className="text-3xl lg:text-4xl font-light mb-6">お気軽にご相談ください</h3>
              <p className="text-base text-white/80 mb-8 leading-relaxed">
                岡山市北区で志望校合格を目指すなら、おかやま学習塾にお任せください。無料体験授業も随時受付中です。
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:0860123456" className="text-white text-xl font-semibold hover:text-[#A5D6A7] transition-colors whitespace-nowrap">
                  <i className="ri-phone-fill text-xl mr-2"></i>
                  <span>086-012-3456</span>
                </a>
                <a href="https://line.me/R/ti/p/@okayamajuku" target="_blank" rel="noopener noreferrer" className="bg-white text-[#1B5E3F] px-6 py-3 rounded-full hover:bg-white/90 transition-colors text-center font-semibold whitespace-nowrap">
                  <i className="ri-line-fill mr-2"></i>LINEで相談
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-base font-semibold mb-6 text-white/70">サイトマップ</h4>
              <ul className="space-y-4">
                <li><Link to="/services" className="text-base hover:text-[#A5D6A7] transition-colors underline">事業内容</Link></li>
                <li><Link to="/about" className="text-base hover:text-[#A5D6A7] transition-colors underline">会社概要</Link></li>
                <li><Link to="/careers" className="text-base hover:text-[#A5D6A7] transition-colors underline">求人情報</Link></li>
                <li><Link to="/blog" className="text-base hover:text-[#A5D6A7] transition-colors underline">ブログ・新着情報</Link></li>
                <li><Link to="/trial" className="text-base hover:text-[#A5D6A7] transition-colors underline">体験申し込み</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-base font-semibold mb-6 text-white/70">おかやま学習塾</h4>
              <p className="text-base text-white/80 mb-2">営業時間：15:00 〜 22:00</p>
              <p className="text-base text-white/80 mb-6">定休日：日曜日・祝日</p>
              <h4 className="text-base font-semibold mb-4 text-white/70">所在地</h4>
              <p className="text-base text-white/80">〒700-0000</p>
              <p className="text-base text-white/80">岡山市北区○○町1-2-3</p>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-white/70 text-sm">
              © 2025 おかやま学習塾 All Rights Reserved.
            </div>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-white/70 hover:text-white transition-colors underline">プライバシーポリシー</a>
              <a href="#" className="text-white/70 hover:text-white transition-colors underline">特定商取引法</a>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors underline">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}